/**
 * EU-747 — telemetria do `defaultErrorComponent` (`POST /me/client-errors`).
 *
 * Até este card o frontend do candidato não reportava erro nenhum: a tela
 * "Algo deu errado" só existia como print de suporte, e não dava para dizer
 * quantos candidatos passaram por ela, em qual rota, em qual build nem em qual
 * engine — foi assim que a mensagem de MIME do WebKit ficou invisível por um
 * ciclo inteiro. Toda renderização do `RouterErrorScreen` passa a emitir UM
 * relato; o BFF acrescenta `user-agent` + client hints e vira log alertável
 * (`marker: client_error`).
 *
 * Fire-and-forget: telemetria falhando NUNCA piora a UX do candidato.
 * Sem PII: `route` é só o pathname (nunca query/hash) e nada do body é texto
 * livre digitado pelo candidato.
 */
import fetchClient from '@eureca/candidate-api-sdk/client';
import { candidateEnv } from '@/config/env';

export type ClientErrorClassification = 'chunk' | 'offline' | 'server';

/** Limites do schema Zod do BFF (`ClientErrorBodySchema`) — mais que isso é 400 e não vira log. */
export const CLIENT_ERROR_LIMITS = {
  message: 500,
  name: 100,
  stack: 2000,
  route: 300,
  buildSha: 40,
} as const;

export interface ClientErrorDescription {
  message: string;
  name?: string;
  stack?: string;
}

function truncate(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value;
}

/**
 * Extrai `message`/`name`/`stack` de QUALQUER coisa lançada — `Error`, objeto
 * com `message`, string ou lixo — sempre com `message` não vazia (o schema
 * exige `min(1)`) e dentro dos limites.
 */
export function describeClientError(error: unknown): ClientErrorDescription {
  if (typeof error === 'object' && error !== null) {
    const candidate = error as { message?: unknown; name?: unknown; stack?: unknown };
    if (typeof candidate.message === 'string') {
      const name =
        typeof candidate.name === 'string' && candidate.name ? candidate.name : undefined;
      const message =
        candidate.message.length > 0 ? candidate.message : `(sem mensagem) ${name ?? 'Error'}`;
      return {
        message: truncate(message, CLIENT_ERROR_LIMITS.message),
        ...(name ? { name: truncate(name, CLIENT_ERROR_LIMITS.name) } : {}),
        ...(typeof candidate.stack === 'string' && candidate.stack
          ? { stack: truncate(candidate.stack, CLIENT_ERROR_LIMITS.stack) }
          : {}),
      };
    }
  }
  if (typeof error === 'string') {
    return {
      message: truncate(error.length > 0 ? error : '(string vazia)', CLIENT_ERROR_LIMITS.message),
    };
  }
  let serialized: string;
  try {
    serialized = JSON.stringify(error) ?? String(error);
  } catch {
    serialized = String(error);
  }
  return { message: truncate(serialized, CLIENT_ERROR_LIMITS.message) };
}

/** Só o pathname: query e hash podem carregar token/ids e não interessam ao recorte. */
function pathnameOnly(route: string): string {
  const cut = route.split(/[?#]/, 1)[0] ?? '';
  return cut.startsWith('/') ? cut : `/${cut}`;
}

export interface ReportRouterErrorInput {
  error: unknown;
  classification: ClientErrorClassification;
  /** Rota atual (`window.location.pathname`); query/hash são descartados aqui. */
  route: string;
  online: boolean;
  /** A tela é a repetição do mesmo erro (o retry escalou para reload). */
  repeat: boolean;
}

/**
 * Relata uma renderização do `RouterErrorScreen`. Nunca lança — nem quando o
 * `fetchClient` rejeita, nem quando ele quebra antes de sair.
 */
export async function reportRouterError(input: ReportRouterErrorInput): Promise<void> {
  try {
    const described = describeClientError(input.error);
    await fetchClient({
      method: 'POST',
      url: '/me/client-errors',
      data: {
        classification: input.classification,
        ...described,
        route: truncate(pathnameOnly(input.route), CLIENT_ERROR_LIMITS.route),
        buildSha: truncate(candidateEnv.buildSha, CLIENT_ERROR_LIMITS.buildSha),
        online: input.online,
        repeat: input.repeat,
      },
    });
  } catch {
    // Engolido de propósito — o relato é best-effort.
  }
}
